import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format, getISOWeek } from "date-fns";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { MarketingEvent } from "@/hooks/use-marketing-events";

const EVENT_TYPES = [
  { value: "post", label: "Post" },
  { value: "release", label: "Release" },
  { value: "website", label: "Website" },
  { value: "event", label: "Event" },
  { value: "campaign", label: "Campaign" },
  { value: "general", label: "General" },
];

interface MarketingEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: MarketingEvent | null;
  defaultYear: number;
  defaultWeek?: number;
  defaultDate?: Date;
  onSave: (data: {
    title: string;
    description?: string;
    event_type: string;
    event_date?: string | null;
    year: number;
    week_number: number;
    staff_ids?: string[];
  }) => void;
  onDelete?: () => void;
  isSaving?: boolean;
}

export const MarketingEventModal = ({
  open,
  onOpenChange,
  event,
  defaultYear,
  defaultWeek,
  defaultDate,
  onSave,
  onDelete,
  isSaving,
}: MarketingEventModalProps) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventType, setEventType] = useState("general");
  const [eventDate, setEventDate] = useState<Date | undefined>(undefined);
  const [year, setYear] = useState(defaultYear);
  const [weekNumber, setWeekNumber] = useState(defaultWeek || 1);
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);

  const { data: staffMembers } = useQuery({
    queryKey: ["crm-staff-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crm_staff")
        .select("id, first_name, last_name")
        .order("first_name");
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (event) {
      setTitle(event.title);
      setDescription(event.description || "");
      setEventType(event.event_type);
      setEventDate(event.event_date ? new Date(event.event_date) : undefined);
      setYear(event.year);
      setWeekNumber(event.week_number);
      setSelectedStaffIds(event.event_staff?.map(s => s.staff_id) || []);
    } else {
      setTitle("");
      setDescription("");
      setEventType("general");
      setEventDate(defaultDate);
      setYear(defaultYear);
      setWeekNumber(defaultWeek || 1);
      setSelectedStaffIds([]);
    }
  }, [event, defaultYear, defaultWeek, defaultDate, open]);

  const handleDateSelect = (date: Date | undefined) => {
    setEventDate(date);
    if (date) {
      setYear(date.getFullYear());
      setWeekNumber(getISOWeek(date));
    }
  };

  const toggleStaff = (staffId: string) => {
    setSelectedStaffIds(prev =>
      prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      title,
      description: description || undefined,
      event_type: eventType,
      event_date: eventDate ? format(eventDate, "yyyy-MM-dd") : null,
      year,
      week_number: weekNumber,
      staff_ids: selectedStaffIds,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{event ? "Edit Event" : "Add Event"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Event title"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Event details / narrative..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select value={eventType} onValueChange={setEventType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !eventDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {eventDate ? format(eventDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={eventDate}
                    onSelect={handleDateSelect}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                min={2024}
                max={2030}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="week">Week Number</Label>
              <Input
                id="week"
                type="number"
                value={weekNumber}
                onChange={(e) => setWeekNumber(parseInt(e.target.value))}
                min={1}
                max={53}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Staff Members</Label>
            <div className="border border-border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
              {staffMembers?.length === 0 && (
                <p className="text-sm text-muted-foreground">No staff members found</p>
              )}
              {staffMembers?.map((s) => (
                <div key={s.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`staff-${s.id}`}
                    checked={selectedStaffIds.includes(s.id)}
                    onCheckedChange={() => toggleStaff(s.id)}
                  />
                  <label
                    htmlFor={`staff-${s.id}`}
                    className="text-sm cursor-pointer"
                  >
                    {s.first_name} {s.last_name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter className="gap-2">
            {event && onDelete && (
              <Button type="button" variant="destructive" onClick={onDelete} disabled={isSaving}>
                Delete
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title || isSaving}>
              {isSaving ? "Saving..." : event ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
